import * as React from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { shallowEqual, useSelector } from 'react-redux';

import Tabs from 'antd/es/tabs';

import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import { parseQuery } from '@common/typescript/utils/url';
import { replaceSearchUrl } from '@common/react/components/Utils/Utils';
import ServerPageProvider from '@common/react/components/Core/ServerPageProvider/ServerPageProvider';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import { useMobileView } from '@common/react/hooks/useMobileView';

import { PortalRegistrationStep } from '@commonTuna/react/objects/PortalRegistrationStep';

import { PatientMainForm, UserRegistrationSteps } from '@app/components/Pages/Register/PatientMainForm';
import InsuranceStep from '@app/components/Pages/Register/InsuranceStep';
import DriverLicenseStep from '@app/components/Pages/Register/DriverLicenseStep';
import { User } from '@app/objects/User';
import { SignHipaa } from '@app/components/Pages/Register/SignHipaa';
import { RegistrationFillForms } from '@app/components/Pages/Register/RegistrationFillForms';
import { ApplicationState } from '@app/store';
import Verification from '@app/components/Pages/Register/Verification';
import { BaseItemsPage, TabsPage } from '@app/objects/Page';
import { Insurance } from '@app/objects/Insurance';
import { DrivingLicense } from '@app/objects/DrivingLicense';

import '@app/scss/pages/register.scss';

export type InsuranceStepPage = BaseItemsPage<Insurance>

export type DrivingLicenseStepPage = BaseItemsPage<DrivingLicense>

interface RegistrationPageType extends TabsPage<InsuranceStepPage | DrivingLicenseStepPage> {
	registrationRedirectPath: string;
	redirectUrl: string;
	error: string;
	user: User;
	needVerification: boolean;
}

export const stepNames = [
	{ name: 'info', value: PortalRegistrationStep.Info, caption: 'Information' },
	{ name: 'hipaa', value: PortalRegistrationStep.Hipaa, caption: 'Hipaa Form' },
	{ name: 'insurance', value: PortalRegistrationStep.Insurance, caption: 'Health Insurance' },
	{ name: 'license', value: PortalRegistrationStep.License, caption: 'Driving License' },
	{ name: 'forms', value: PortalRegistrationStep.Forms, caption: 'Consent Forms' },
];

const tabItems = stepNames.map(({ value, caption }) => {
	return {
		key: value.toString(),
		label: caption,
		children: null,
	};
});

export interface PortalSettings {
	askForAvatar: boolean;
	askForInsurance: boolean;
	askForDrivingLicense: boolean;
}

interface ReduxProps {
	userRegistrationSteps: UserRegistrationSteps;
}

interface PatientRegistrationInnerProps extends ReduxProps {
	serverPage: RegistrationPageType;
}

const PatientRegistrationInner: React.FC<PatientRegistrationInnerProps> = (props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const query = parseQuery(location.search);
	const isMobile = useMobileView(768);

	const { serverPage, userRegistrationSteps } = props;

	const getStepFromUrl = () => {
		const stepFromUrl = stepNames.find((s) => s.name === query.step);
		return stepFromUrl
			? stepFromUrl.value
			: PortalRegistrationStep.Info;
	};

	const [currentStep, setCurrentStep] = React.useState<PortalRegistrationStep>(() => {
		if (serverPage.registrationRedirectPath) {
			return PortalRegistrationStep.Info;
		}

		return getStepFromUrl();
	});

	const {
		request,
		getUser,
		updateUser,
	} = useApplicationContext();

	const [steps, setSteps] = React.useState<Array<PortalRegistrationStep>>(
		userRegistrationSteps?.steps
			|| [PortalRegistrationStep.Info, PortalRegistrationStep.Insurance, PortalRegistrationStep.License],
	);
	const [needVerification, setNeedVerification] = React.useState<boolean>(serverPage.needVerification);
	const [initDataAfterVerification, setInitDataAfterVerification] = React.useState(serverPage.user ?? null);

	const user = getUser<User>();

	React.useEffect(() => {
		if (serverPage.registrationRedirectPath) {
			navigate(serverPage.registrationRedirectPath, { replace: true });
		}
	}, []);

	React.useEffect(() => {
		setCurrentStep(getStepFromUrl());
	}, [location.search]);

	const resTabItems = React.useMemo(() => {
		const enableItems = tabItems
			.filter((item) => steps.some((q) => q.toString() === item.key));
		const currentStepIndex = enableItems.findIndex((item) => item.key === currentStep?.toString());
		return enableItems
			.map((item, i) => ({
				...item,
				visible: steps.some((q) => q.toString() === item.key) && (!isMobile || i === currentStepIndex || i === currentStepIndex + 1),
				disabled: (!user && item.key !== PortalRegistrationStep.Info.toString()) || (currentStepIndex > 0 && currentStepIndex > i),
			}))
			.filter((item) => item.visible)
			.map((item, i, arr) => ({
				...item,
				label: <>
					{item.label}
					{i === arr.length - 1 ? null : <div className="lines">
						<div className="left" />
					</div>}
				</>,
			}));
	}, [currentStep, user, isMobile]);

	const completeRegistration = (_user: User) => {
		const redirect = () => {
			if (query.redirectUrl) {
				navigate(`/${query.redirectUrl}`);
			} else {
				navigate('/dashboard');
			}
		};

		if (_user.completedRegistration) {
			redirect();
		} else {
			request<User>('userCompletedRegistration', {
				id: _user.id || 0,
				completedRegistration: true,
			}).then((res) => {
				updateUser({ completedRegistration: res.completedRegistration });

				redirect();
			});
		}
	};

	const setNextStep = (rSteps?: Array<PortalRegistrationStep>, rUser?: User) => {
		if (rSteps) {
			setSteps(rSteps);
		} else if (rUser?.completedRegistration) {
			completeRegistration(rUser);
			return;
		}

		if (currentStep === null) return;

		const _steps = rSteps || steps;

		const index = _steps.indexOf(currentStep);

		if (index === _steps.length - 1) {
			completeRegistration(user || rUser);
			return;
		}

		const nextStep = _steps[index + 1];

		const step = stepNames.find((s) => s.value === nextStep);
		navigate({
			...location,
			search: replaceSearchUrl(location.search, 'step', step.name),
		}, { replace: true });
	};

	const renderButtons = (handleSubmit: () => void) => {
		return <div className="registration-buttons">
			<button type="button" className="btn btn-primary" onClick={handleSubmit}>Next</button>
			{/* <button type="button" className="btn btn-next btn-default" onClick={setNextStep}>Next</button> */}
		</div>;
	};

	const getInitialItems: <T>(page: BaseItemsPage<T>, type: string) => Array<T> | undefined = (page, type) => {
		return page && page._type === type
			? page?.items?.list
			: undefined;
	};

	const getCurrentStep = (serverPage: RegistrationPageType) => {
		const innerPage = serverPage.innerPage;

		if (needVerification) {
			return <Verification
				token={query.token}
				emailCode={query.ecode}
				phoneCode={query.pcode}
				afterSubmit={(item) => {
					setInitDataAfterVerification(item);
					setNeedVerification(false);
				}}
			/>;
		}

		switch (currentStep) {
			case PortalRegistrationStep.Info:
				return <PatientMainForm
					initDate={initDataAfterVerification}
					token={query.token}
					afterSubmit={setNextStep}
				/>;
			case PortalRegistrationStep.Hipaa:
				return user && <SignHipaa
					renderButtons={renderButtons}
					afterSubmit={() => setNextStep()}
				/>;
			// case PortalRegistrationStep.Photo:
			// return user && <PatientPhoto renderButtons={renderButtons} user={user} afterSubmit={setNextStep} />;
			case PortalRegistrationStep.Insurance:
				return user && <InsuranceStep
					initialItems={getInitialItems<Insurance>(innerPage as InsuranceStepPage, 'InsuranceRegistrationPage')}
					renderButtons={renderButtons}
					afterSubmit={setNextStep}
				/>;
			case PortalRegistrationStep.License:
				return user && <DriverLicenseStep
					initialItems={getInitialItems<DrivingLicense>(innerPage as DrivingLicenseStepPage, 'LicenseRegistrationPage')}
					renderButtons={renderButtons}
					afterSubmit={setNextStep}
				/>;
			case PortalRegistrationStep.Forms:
				return user && <RegistrationFillForms
					renderButtons={renderButtons}
					afterSubmit={() => setNextStep()}
				/>;
			default:
				return <Loader />;
		}
	};

	return (
		<main className="container">
			{currentStep !== PortalRegistrationStep.Info && user === null && <Navigate to="/" />}
			<div className="patient-register-page__form" style={{ maxWidth: needVerification ? 'min(100%, 420px)' : undefined }}>
				<div className="form-box">
					<div className="register-headline text-center form-box__headline" style={{ border: needVerification ? undefined : 'none' }}>
						<h1>{needVerification ? 'VERIFICATION' : 'REGISTRATION'}</h1>
					</div>
					{!needVerification && <Tabs
						id="registration-steps"
						activeKey={currentStep.toString()}
						className={`registration-tabs ${currentStep === +resTabItems[resTabItems.length - 1].key
							? 'registration-tabs__last' : ''}`}
						items={resTabItems}
					/>}
				</div>
				{getCurrentStep(serverPage)}
			</div>
		</main>
	);
};

const PatientRegistration: React.FC = () => {
	const {
		userRegistrationSteps,
	} = useSelector((state: ApplicationState) => ({
		userRegistrationSteps: state.userRegistrationSteps.item,
	}), shallowEqual);

	return <>
		<div className="patient-register-page">
			<ServerPageProvider<RegistrationPageType>
				path="registration"
				defaultTitle="Registration"
				inner={(serverPage) => {
					return <PatientRegistrationInner
						userRegistrationSteps={userRegistrationSteps}
						serverPage={serverPage}
					/>;
				}}
			/>
		</div>
	</>;
};

export default PatientRegistration;
